/**
 * Staff roles, read from the access token Keycloak issued.
 *
 * Not from the user document. The back office used to gate on a `role` string
 * carried on the profile, which is a value this application stores and can
 * therefore be talked into changing; the backend meanwhile compared the same
 * field on a request object where it was never set, so the admin API answered
 * 403 to everybody. Both halves now read the realm roles in the token, which
 * only Keycloak can put there.
 *
 * This is display gating. The server enforces the same roles on every admin
 * endpoint (`requireAnyRole`, `backend/src/middleware/keycloakAuth.ts`), and a
 * forged token fails signature validation there. Hiding a button has never been
 * the control.
 */

/** Mirrors ADMIN_ROLES in backend/src/middleware/keycloakAuth.ts. */
export const ADMIN_ROLES = ["staff_admin"];

interface AccessTokenClaims {
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
}

/**
 * The claims of a JWT, without verifying it.
 *
 * Verification happens at the backend, which is the only place it means
 * anything. Reading here is how the navigation knows what to draw.
 */
function claimsOf(token: string | null): AccessTokenClaims | null {
  if (!token) return null;

  const payload = token.split(".")[1];
  if (!payload) return null;

  try {
    // base64url, and atob wants base64 with padding.
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    return JSON.parse(decodeURIComponent(escape(atob(padded))));
  } catch {
    return null;
  }
}

/** Every realm and client role the token carries. */
export function rolesFromToken(token: string | null): string[] {
  const claims = claimsOf(token);
  if (!claims) return [];

  const realm = claims.realm_access?.roles ?? [];
  const client = Object.values(claims.resource_access ?? {}).flatMap(
    (entry) => entry?.roles ?? [],
  );
  return [...realm, ...client];
}

export function hasStaffRole(token: string | null, role: string): boolean {
  return rolesFromToken(token).includes(role);
}

export function isStaffAdmin(token: string | null): boolean {
  const held = rolesFromToken(token);
  return ADMIN_ROLES.some((role) => held.includes(role));
}
